import styled from 'styled-components';
import { themeGet } from '@styled-system/theme-get';
import { useTranslation } from 'react-i18next';

import Text from '../../../../shared/Text';
import { Container, Section, SectionBody } from './StyledComponents';
import { useGetPostTiles } from '../Posts/hooks/useGetPostTiles';
import { makeGetRecipientsCount, formatDate } from './helpers';
import { EMAIL, MESSAGE, SMS } from '../../utils/constants';
import { NotConfigured } from './NotConfigured.jsx';
import { PostPreview } from './PostPreview.jsx';

export const ReviewStep = ({ post, messageForm, previousStep }) => {
  const tiles = useGetPostTiles({ uuid: post.uuid });
  const { t } = useTranslation('postNotifications');

  const { message, values } = messageForm;

  const getRecipientsCount = makeGetRecipientsCount(values, message);

  const skippedNotification = previousStep === MESSAGE;
  const notificationNotConfigured =
    !messageForm.dirty && skippedNotification && values.id.length === 0;
  const reminderNotConfigured = !post.reminderDate;

  return (
    <ReviewContainer>
      <ReviewSection>
        <Text padding="14px 24px" as="div" variant="h6">
          {t('reviewStep.title')}
        </Text>
        <ReviewSectionBody>
          <Summary>
            <Text as="div" variant="h7">
              {t('reviewStep.summaryTitle')}
            </Text>
            <Content>
              <TitleWrapper>
                <Text variant="subtitle3">
                  {t('reviewStep.initialNotification')}
                </Text>
                {notificationNotConfigured && <NotConfigured />}
              </TitleWrapper>

              <Text variant="body2" margin="0 0 4px">
                {notificationNotConfigured
                  ? t('reviewStep.notificationNotConfigured')
                  : t('reviewStep.notificationConfigured')}
              </Text>
              <UnorderedList>
                <li>
                  <Text variant="button">
                    {formatDate(
                      post.publishDate,
                      post.sendTime,
                      post.boardTimezone,
                    )}
                  </Text>
                </li>
              </UnorderedList>
            </Content>
            <Content>
              <TitleWrapper>
                <Text variant="subtitle3">{t('reviewStep.reminders')}</Text>
                {reminderNotConfigured && <NotConfigured />}
              </TitleWrapper>
              {reminderNotConfigured ? (
                <Text variant="body2">
                  {t('reviewStep.remindersNotConfigured')}
                </Text>
              ) : (
                <>
                  <Text variant="body2" margin="0 0 4px">
                    {t('reviewStep.remindersConfigured')}
                  </Text>
                  <UnorderedList>
                    <li>
                      <Text variant="button">
                        {formatDate(
                          values.reminderDate,
                          post.reminderSendTime,
                          post.boardTimezone,
                        )}
                      </Text>
                    </li>
                  </UnorderedList>
                </>
              )}
            </Content>
            {!notificationNotConfigured && (
              <AudienceBreakdown>
                <Text variant="subtitle3">
                  {t('reviewStep.audienceBreakdown')}
                </Text>
                <UnorderedList>
                  <li>
                    <Text variant="button">
                      {t('reviewStep.emailRecipientsCount', {
                        count: getRecipientsCount(EMAIL),
                      })}
                    </Text>
                  </li>
                  <li>
                    <Text variant="button">
                      {t('reviewStep.textRecipientsCount', {
                        count: getRecipientsCount(SMS),
                      })}
                    </Text>
                  </li>
                </UnorderedList>
                <Text variant="body2">{t('reviewStep.note')}</Text>
              </AudienceBreakdown>
            )}
          </Summary>
        </ReviewSectionBody>
        <PostPreview tiles={tiles} />
      </ReviewSection>
    </ReviewContainer>
  );
};

const ReviewSection = styled(Section)`
  width: 676px;
`;

const ReviewContainer = styled(Container)`
  margin: 0 auto;
  gap: 0;

  &:after {
    content: '';
    display: block;
    min-height: 24px;
  }
`;

const ReviewSectionBody = styled(SectionBody)`
  padding: 24px;
`;

const Summary = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
`;

const TitleWrapper = styled.div`
  margin: 0 0 8px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const AudienceBreakdown = styled.div`
  display: flex;
  padding: 16px;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  align-self: stretch;
  border-radius: 8px;
  background-color: ${themeGet('colors.black.5')};
`;

const UnorderedList = styled.ul`
  list-style-position: inside;
  list-style-type: initial;
`;
